import { supportedBrandingType } from './TypeDefinitions';
import { Logger } from '../utils/Logger';
import { logConstants } from '../../utils/Constants';
import { StringMap } from '../../utils/TypeDefinitions';
import { AppName } from '../../utils/Constants';
import { NativePlatform } from '../../workspace/Constants';
import { GACSDiscoveryRegion } from '../../Common/TypeDefinitions';

export interface InstalledPluginInfo {
	name: string;
	version: string;
}
export interface ConfigJSON {
	locale: string;
	isHttpAllowed: boolean;
	debugMode: boolean;
	nativeLoggingEnabled: boolean;
	allowCustomPortalConfig: boolean; //Forces all urls to be added as a custom portal
	allowAppConfigStaging: boolean;
	brandingName: string;
	brandingType: supportedBrandingType;
	appConfigEnabled: boolean;
	isUnifiedAuthenticationEnabled?: boolean;
	isSimplifyBeaconLogicEnabled?: boolean;
	isAuthenticatedGlobalAppConfigServiceEnabled?: boolean;
	clientVersion?: string;
	osVersion?: string;
	installedPluginsInfo?: InstalledPluginInfo[];
	clientId?: string;
	isNewUXBrowserModeEnabled?: boolean;
	hostApp: AppName;
	platform: NativePlatform;
	shouldShowSmartCardToggle?: boolean;
	shouldShowWebInterfaceToggle?: boolean;
	gacsDevelopUrl?: string;
	gacsDiscoveryRegion: GACSDiscoveryRegion;
	appconfigGetDiscoveryDocumentEnabled: boolean;
}
export class AccountManager {
	private static _instance: AccountManager;

	configJSON: ConfigJSON = {
		locale: 'en',
		isHttpAllowed: false,
		debugMode: false,
		nativeLoggingEnabled: true,
		allowCustomPortalConfig: false,
		allowAppConfigStaging: false,
		brandingName: 'Citrix Workspace',
		brandingType: 'none',
		appConfigEnabled: true,
		isUnifiedAuthenticationEnabled: false,
		isSimplifyBeaconLogicEnabled: false,
		isAuthenticatedGlobalAppConfigServiceEnabled: false,
		clientVersion: '',
		osVersion: '',
		installedPluginsInfo: [],
		clientId: '',
		isNewUXBrowserModeEnabled: false,
		hostApp: AppName.WORKSPACE,
		platform: NativePlatform.Windows,
		shouldShowSmartCardToggle: false,
		shouldShowWebInterfaceToggle: false,
		gacsDevelopUrl: '',
		gacsDiscoveryRegion: GACSDiscoveryRegion.DEFAULT,
		appconfigGetDiscoveryDocumentEnabled: false,
	};

	private constructor() {} // eslint-disable-line @typescript-eslint/no-empty-function

	static get Instance(): AccountManager {
		return AccountManager._instance || (AccountManager._instance = new AccountManager());
	}

	/**
	 * parses and stores config sent by native
	 *
	 * @param {string} initData
	 * @memberof AccountManager
	 */
	initializeConfig(initData: StringMap): void {
		Logger.getInstance().write(
			logConstants.INFO,
			`accountConfigInitData: ${JSON.stringify(initData)}`
		);
		if (initData.isNewUXBrowserModeEnabled) {
			this.configJSON.isNewUXBrowserModeEnabled = JSON.parse(
				initData.isNewUXBrowserModeEnabled.toLowerCase()
			);
		}
		if (initData.locale) {
			const lng = initData.locale === 'pt' ? 'pt-BR' : initData.locale;
			this.configJSON.locale = lng;
		}
		if (initData.isHttpAllowed) {
			this.configJSON.isHttpAllowed = JSON.parse(initData.isHttpAllowed.toLowerCase());
		}
		if (initData.debugMode) {
			this.configJSON.debugMode = JSON.parse(initData.debugMode.toLowerCase());
		}
		if (initData.nativeLoggingEnabled) {
			this.configJSON.nativeLoggingEnabled = JSON.parse(
				initData.nativeLoggingEnabled.toLowerCase()
			);
		}
		if (initData.allowCustomPortalConfig) {
			this.configJSON.allowCustomPortalConfig = JSON.parse(
				initData.allowCustomPortalConfig.toLowerCase()
			);
		}
		if (initData.allowAppConfigStaging) {
			this.configJSON.allowAppConfigStaging = JSON.parse(
				initData.allowAppConfigStaging.toLowerCase()
			);
		}
		if (initData.brandingName) {
			this.configJSON.brandingName = initData.brandingName;
		}
		if (initData.brandingType) {
			this.configJSON.brandingType =
				initData.brandingType.toLowerCase() as supportedBrandingType;
		}
		if (initData.appConfigEnabled) {
			this.configJSON.appConfigEnabled = JSON.parse(
				initData.appConfigEnabled.toLowerCase()
			);
		}
		if (initData.isUnifiedAuthenticationEnabled) {
			this.configJSON.isUnifiedAuthenticationEnabled = JSON.parse(
				initData.isUnifiedAuthenticationEnabled.toLowerCase()
			);
		}
		if (initData.isSimplifyBeaconLogicEnabled) {
			this.configJSON.isSimplifyBeaconLogicEnabled = JSON.parse(
				initData.isSimplifyBeaconLogicEnabled.toLowerCase()
			);
		}
		if (initData.isAuthenticatedGlobalAppConfigServiceEnabled) {
			const parsedValue = JSON.parse(
				initData.isAuthenticatedGlobalAppConfigServiceEnabled.toLowerCase()
			);
			this.configJSON.isAuthenticatedGlobalAppConfigServiceEnabled =
				this.configJSON.appConfigEnabled && parsedValue;
		}
		if (initData.clientVersion) {
			this.configJSON.clientVersion = initData.clientVersion;
		}
		if (initData.osVersion) {
			this.configJSON.osVersion = initData.osVersion;
		}
		if (initData.installedPluginsInfo) {
			this.configJSON.installedPluginsInfo = JSON.parse(
				initData.installedPluginsInfo
			) as InstalledPluginInfo[];
		}
		if (initData.clientId) {
			this.configJSON.clientId = initData.clientId;
		}
		if (initData.hostApp) {
			this.configJSON.hostApp = initData.hostApp as AppName;
		}
		if (initData.platform) {
			this.configJSON.platform = initData.platform as NativePlatform;
		}

		if (initData.shouldShowSmartCardToggle) {
			this.configJSON.shouldShowSmartCardToggle = JSON.parse(
				initData.shouldShowSmartCardToggle.toLowerCase()
			);
		}

		if (initData.shouldShowWebInterfaceToggle) {
			this.configJSON.shouldShowWebInterfaceToggle = JSON.parse(
				initData.shouldShowWebInterfaceToggle.toLowerCase()
			);
		}
		if (initData.gacsDevelopUrl) {
			this.configJSON.gacsDevelopUrl = initData.gacsDevelopUrl;
		}
		if (initData.gacsDiscoveryRegion) {
			//This is coming in from Native , and is set via GPO in CWA windows.
			this.configJSON.gacsDiscoveryRegion =
				initData.gacsDiscoveryRegion.toLowerCase() as GACSDiscoveryRegion;
		}
		if (initData.appconfigGetDiscoveryDocumentEnabled) {
			this.configJSON.appconfigGetDiscoveryDocumentEnabled = JSON.parse(
				initData.appconfigGetDiscoveryDocumentEnabled.toLowerCase()
			);
		}
	}
}
